import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TypeBadge = ({ type }) => {
  const map = {
    credit: 'bg-success/10 text-success',
    debit: 'bg-error/10 text-error',
  };
  return (
    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${map?.[type] || 'bg-muted text-muted-foreground'}`}>
      {type === 'credit' ? '+ Credit' : '- Debit'}
    </span>
  );
};

const RewardsLedger = ({ currentAdminId }) => {
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [totals, setTotals] = useState({ totalCredits: 0, totalDebits: 0, netBalance: 0 });

  const filtered = ledger?.filter(l => {
    const matchSearch = l?.userName?.toLowerCase()?.includes(search?.toLowerCase()) || l?.campaign?.toLowerCase()?.includes(search?.toLowerCase()) || l?.refId?.toLowerCase()?.includes(search?.toLowerCase());
    const matchType = typeFilter === 'all' || l?.type === typeFilter;
    return matchSearch && matchType;
  });

  const loadLedger = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('wallet_ledger')
        ?.select(`id, ledger_type, amount, balance_after, description, reference_type, created_at,
          user:user_profiles!wallet_ledger_user_id_fkey(full_name, email),
          transaction:transactions!wallet_ledger_transaction_id_fkey(id, campaigns(title))`)
        ?.order('created_at', { ascending: false })
        ?.limit(200);
      if (!error && data) {
        const mapped = data?.map(l => ({
          id: l?.id,
          userName: l?.user?.full_name || l?.user?.email?.split('@')?.[0] || 'User',
          userEmail: l?.user?.email || '',
          campaign: l?.transaction?.campaigns?.title || l?.description || '—',
          type: l?.ledger_type,
          amount: parseFloat(l?.amount || 0),
          balanceAfter: parseFloat(l?.balance_after || 0),
          description: l?.description || '',
          referenceType: l?.reference_type || '',
          date: new Date(l?.created_at)?.toLocaleDateString(),
          dateTime: new Date(l?.created_at)?.toLocaleString(),
          refId: l?.id?.split('-')?.[0]?.toUpperCase(),
        }));
        setLedger(mapped);

        const credits = mapped?.filter(l => l?.type === 'credit')?.reduce((s, l) => s + l?.amount, 0);
        const debits = mapped?.filter(l => l?.type === 'debit')?.reduce((s, l) => s + l?.amount, 0);
        setTotals({ totalCredits: credits, totalDebits: debits, netBalance: credits - debits });
      }
    } catch (err) {
      console.error('[RewardsLedger] loadLedger error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLedger(); }, [loadLedger]);

  const handleExport = () => {
    const headers = ['Ref ID', 'User', 'Email', 'Campaign', 'Type', 'Amount', 'Balance After', 'Date'];
    const rows = filtered?.map(l => [l?.refId, l?.userName, l?.userEmail, l?.campaign, l?.type, l?.amount?.toFixed(2), l?.balanceAfter?.toFixed(2), l?.dateTime]);
    const csv = [headers, ...rows]?.map(r => r?.map(c => `"${String(c)?.replace(/"/g, '""')}"`)?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ledger_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body?.appendChild(a); a?.click(); document.body?.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Credits', value: `$${totals?.totalCredits?.toFixed(2)}`, icon: 'TrendingUp', color: 'text-success', bg: 'bg-success/10' },
          { label: 'Total Debits', value: `$${totals?.totalDebits?.toFixed(2)}`, icon: 'TrendingDown', color: 'text-error', bg: 'bg-error/10' },
          { label: 'Net Balance', value: `$${totals?.netBalance?.toFixed(2)}`, icon: 'Wallet', color: 'text-primary', bg: 'bg-primary/10' },
        ]?.map((s, i) => (
          <div key={i} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s?.bg}`}>
              <Icon name={s?.icon} size={20} className={s?.color} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{s?.label}</p>
              <p className={`text-xl font-bold ${s?.color}`}>{s?.value}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-foreground">Wallet Ledger</h3>
              <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-md font-medium">Immutable</span>
            </div>
            <p className="text-sm text-muted-foreground">{filtered?.length} records</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-48"><Input type="search" placeholder="Search ledger..." value={search} onChange={(e) => setSearch(e?.target?.value)} /></div>
            <div className="w-36">
              <Select
                options={[{ value: 'all', label: 'All Types' }, { value: 'credit', label: 'Credits' }, { value: 'debit', label: 'Debits' }]}
                value={typeFilter} onChange={setTypeFilter}
              />
            </div>
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={handleExport}>Export CSV</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {['Ref ID', 'User', 'Campaign', 'Type', 'Amount', 'Balance After', 'Date']?.map(h => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered?.map((l) => (
                  <tr key={l?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-3">
                      <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">{l?.refId}</span>
                    </td>
                    <td className="py-3 px-3">
                      <p className="text-sm font-medium text-foreground">{l?.userName}</p>
                      <p className="text-xs text-muted-foreground">{l?.userEmail}</p>
                    </td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground max-w-[140px] truncate block">{l?.campaign}</span></td>
                    <td className="py-3 px-3"><TypeBadge type={l?.type} /></td>
                    <td className="py-3 px-3">
                      <span className={`text-sm font-bold ${l?.type === 'credit' ? 'text-success' : 'text-error'}`}>
                        {l?.type === 'credit' ? '+' : '-'}${l?.amount?.toFixed(2)}
                      </span>
                    </td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground">${l?.balanceAfter?.toFixed(2)}</span></td>
                    <td className="py-3 px-3"><span className="text-xs text-muted-foreground">{l?.date}</span></td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr><td colSpan={7} className="py-10 text-center text-sm text-muted-foreground">No ledger records found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardsLedger;
